const BASE  = 'https://api.themoviedb.org/3'
const TOKEN = import.meta.env.VITE_TMDB_TOKEN

const headers = {
  Authorization: `Bearer ${TOKEN}`,
  'Content-Type': 'application/json',
}

// Two-tier cache: in-memory (fast) + sessionStorage (survives page refresh)
const cache = new Map()
const CACHE_TTL  = 5 * 60 * 1000   // 5 min in-memory
const SS_TTL     = 20 * 60 * 1000  // 20 min sessionStorage
const SS_PREFIX  = 'tmdb_'

// Session-persistent cache for /tv/{id} detail GETs. Unlike the general 5-min
// `cache`, these entries NEVER expire within a session — during a bulk import
// (hundreds of shows, and the same show can appear across multiple rows/sources)
// re-resolving a show must never re-hit TMDB and burn rate-limit budget. Keyed by
// the full request URL, so append_to_response variants stay distinct.
const tvCache = new Map()

// Same-tick request coalescing — see the note in tmdbGet.
const inflight = new Map()
const isTvDetail = (path) => /^\/tv\/\d+$/.test(String(path).split('?')[0])

// A TMDB failure that means "couldn't reach the API" (rate-limit exhaustion,
// upstream 5xx, network/timeout) — as opposed to a genuine 4xx no-match. Importers
// use this to report throttled rows as "try again" instead of "couldn't match".
export function isTmdbReachError(e) {
  if (!e) return false
  if (e.name === 'AbortError' || e.name === 'TypeError') return true
  return /\b429\b|TMDB 5\d\d|network|fetch|timeout|abort/i.test(String(e.message || e))
}

function ssGet(key) {
  try {
    const raw = sessionStorage.getItem(SS_PREFIX + key)
    if (!raw) return null
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > SS_TTL) { sessionStorage.removeItem(SS_PREFIX + key); return null }
    return data
  } catch { return null }
}

function ssSet(key, data) {
  try { sessionStorage.setItem(SS_PREFIX + key, JSON.stringify({ data, ts: Date.now() })) } catch {}
}

// Drop every cached copy of one path so the next tmdbGet goes to the network and
// re-populates the caches for everyone. The /tv/{id} tvCache has no TTL by design
// (a show's detail rarely changes within a session), which is exactly wrong for the
// stale-schedule sweep in a long-resident app: it would read back the snapshot it
// is trying to refresh. Evict + normal get beats noCache, which skips the writes.
export function tmdbEvict(path, params = {}) {
  const url = new URL(`${BASE}${path}`)
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, String(v)))
  const key = url.toString()
  tvCache.delete(key)
  cache.delete(key)
  try { sessionStorage.removeItem(SS_PREFIX + key) } catch {}
}

export async function tmdbGet(path, params = {}, options = {}) {
  const url = new URL(`${BASE}${path}`)
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, String(v)))
  const key = url.toString()

  // Check cache: session-persistent /tv detail cache first, then in-memory, then sessionStorage
  if (!options.noCache) {
    if (isTvDetail(path)) {
      const tv = tvCache.get(key)
      if (tv) return tv
    }
    const hit = cache.get(key)
    if (hit && Date.now() - hit.ts < CACHE_TTL) return hit.data
    const ssHit = ssGet(key)
    if (ssHit) { cache.set(key, { data: ssHit, ts: Date.now() }); return ssHit }

    // In-flight dedupe. The caches above are only WRITTEN after the fetch resolves, so
    // two callers asking for the same URL in the same tick both miss and both hit the
    // network — and concurrent same-key calls are the normal case here, not the
    // exception: Home mounts ~15 fetching components at once and Calendar fans out one
    // /tv/{id} per watchlist row. Duplicates were pure waste AND pushed us toward
    // TMDB's 429, whose backoff costs seconds (see the retry loop below).
    // Returning the SAME promise is what makes it work; the winner populates the cache
    // for everyone. (Perf audit 2026-08-01.)
    const live = inflight.get(key)
    if (live) return live
  }

  const run = async () => {
  // Resilience (audit 2026-07-06): every attempt has a hard timeout (a half-open
  // socket on flaky mobile used to hang the awaiting UI for minutes); transient
  // failures (5xx + network/timeout) are retried with capped backoff, not just 429;
  // and a server-supplied Retry-After is clamped so a proxy can't pin the spinner.
  const TIMEOUT_MS = 9000
  const MAX_ATTEMPTS = 3
  const backoff = (n) => new Promise(res => setTimeout(res, Math.min(500 * 2 ** n, 4000) + Math.random() * 200))
  let lastErr = null

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const ctl = new AbortController()
    const timer = setTimeout(() => ctl.abort(), TIMEOUT_MS)
    let r
    try {
      r = await fetch(key, { headers, signal: ctl.signal })
    } catch (e) {
      // Network error or our own timeout abort — transient, retry.
      clearTimeout(timer)
      lastErr = e
      await backoff(attempt)
      continue
    }
    clearTimeout(timer)

    // Rate limited — the surge case. Back off EXPLICITLY and exponentially: honor a
    // server-supplied Retry-After, but never wait less than an exponential floor that
    // grows per attempt (~2s, 4s, 8s), and clamp so a hostile proxy can't pin us. A
    // longer, escalating wait here is what keeps a TV-Time-migration flood from
    // hammering TMDB into a sustained 429 wall.
    if (r.status === 429) {
      const ra    = parseInt(r.headers.get('Retry-After') || '0', 10)
      const raMs  = Number.isFinite(ra) && ra > 0 ? ra * 1000 : 0
      const expMs = Math.min(2000 * 2 ** attempt, 12000)   // 2s → 4s → 8s
      const wait  = Math.min(Math.max(raMs, expMs), 15000) + Math.random() * 400
      lastErr = new Error(`TMDB 429: ${path}`)
      await new Promise(res => setTimeout(res, wait))
      continue
    }

    // 5xx is a transient upstream hiccup — retry (a single 502 used to surface as
    // a permanent "Show not found"). 4xx (404 etc.) is permanent — fail fast.
    if (r.status >= 500) {
      lastErr = new Error(`TMDB ${r.status}: ${path}`)
      await backoff(attempt)
      continue
    }
    if (!r.ok) throw new Error(`TMDB ${r.status}: ${path}`)

    const data = await r.json()
    if (!options.noCache) {
      cache.set(key, { data, ts: Date.now() })
      ssSet(key, data)
      if (isTvDetail(path) && data?.id) tvCache.set(key, data)
    }
    return data
  }

  throw lastErr || new Error(`TMDB request failed: ${path}`)
  }

  // Publish the in-flight promise BEFORE awaiting it, so a caller in the same tick
  // joins this request instead of starting a second one. Cleared on settle either way
  // — a failure must not be memoized, or one flaky request would poison the key for
  // the rest of the session. noCache callers stay fully independent by design.
  if (options.noCache) return run()
  const p = run().finally(() => inflight.delete(key))
  inflight.set(key, p)
  return p
}

// ── Related shows ─────────────────────────────────────────────────────────────
// Use /recommendations, NOT /similar.
//
// TMDB has two "you might also like" endpoints and they are not close in quality.
// /similar matches on keywords and genres; /recommendations is built from what
// people who watched one show actually went on to watch. Measured on real seeds
// from Jason's list (2026-07-31):
//
//   Breaking Bad  similar → The Spy (2019), Mistress, The Lovers
//                 recs    → BETTER CALL SAUL, Narcos, Snowfall
//   Severance     similar → This Life (1996), Thriller (1973)
//                 recs    → Tales from the Loop, The Institute, The Capture
//   Squid Game    similar → Thriller (1973), Kyle XY (2006)
//                 recs    → Citadel, Taxi Driver (2021), Panic
//
// /similar doesn't return Better Call Saul for Breaking Bad at all. That endpoint
// is why Discover's flagship shelf was serving 1973 and 1996 obscurities to a user
// whose list is Squid Game and Severance.
//
// /similar isn't deleted, it's demoted: recommendations can be thin or empty for an
// obscure or brand-new show, and one lonely card is worse than a padded row. Below
// MIN_STRONG we top up rather than replace, so the good picks still lead.
const MIN_STRONG = 4
export async function relatedShows(showId) {
  const rec  = await tmdbGet(`/tv/${showId}/recommendations`).catch(() => null)
  const strong = (rec?.results || []).filter(s => s?.id)
  if (strong.length >= MIN_STRONG) return strong
  const sim = await tmdbGet(`/tv/${showId}/similar`).catch(() => null)
  const seen = new Set(strong.map(s => s.id))
  return [...strong, ...(sim?.results || []).filter(s => s?.id && !seen.has(s.id))]
}

export const posterUrl   = (p, size = 'w342') => p ? `https://image.tmdb.org/t/p/${size}${p}` : null

// Pick a poster size from what the card ACTUALLY renders at, times the screen's
// pixel ratio — instead of hard-coding one size and hoping.
//
// This exists because hard-coding went wrong in both directions. ShowCard was set
// to w185 with the reasoning "the card renders ~140px, so w185 covers it even at
// ~1.3× DPR" — but this is a phone-first app and phones are 2×–3×, so a 140px card
// needed 280–420 device px and the browser upscaled a 185px JPEG. Measured on
// Discover 2026-07-31: 56 of 142 images under-resolved, all 9 rails soft while the
// w780 backdrop above them was crisp. That mismatch is a good part of what made
// Discover read as "a version behind" (Jason 2026-07-23).
//
// Capped at 2× deliberately: beyond that the gain is invisible on a 110px poster
// and the bytes are not. At 1× (desktop) this asks for LESS than the old constant,
// so the perf pass's intent survives on the devices it was actually right for.
//
// Sizes are TMDB's real poster ladder — a width that isn't on it 404s.
const POSTER_STEPS = [92, 154, 185, 342, 500, 780]
export const posterFor = (p, cssWidth) => {
  if (!p) return null
  const dpr    = (typeof window !== 'undefined' && window.devicePixelRatio) || 1
  const needed = cssWidth * Math.min(dpr, 2)
  const size   = POSTER_STEPS.find(s => s >= needed) || POSTER_STEPS[POSTER_STEPS.length - 1]
  return `https://image.tmdb.org/t/p/w${size}${p}`
}
export const backdropUrl = (p, size = 'w780') => p ? `https://image.tmdb.org/t/p/${size}${p}` : null
